import React, { useContext } from 'react'
import styled from 'styled-components/macro'
import estyled from '@emotion/styled'
import {
  GelHeading2,
  GelParagraph,
  GelHeading6,
  GelHeading4,
  GelScreenDetectorContext
} from '@tal-gel/components'
import { getGelTokens } from '@tal-gel/theming'

import note from './assets/note.svg'
import noteWhite from './assets/note-white.svg'
import health from './assets/health.svg'
import healthWhite from './assets/health-white.svg'
import checks from './assets/checks.svg'
import checksWhite from './assets/checks-white.svg'
import comboImage from './assets/combo-images-dots.png'

const why = [
  {
    number: 1,
    text: 'They can detect conditions early before signs and symptoms'
  },
  {
    number: 2,
    text: 'They can prevent conditions such as bowel cancer, skin cancer and heart disease'
  },
  {
    number: 3,
    text: 'They’re easy to do, most only take a few minutes and some can even be done at home'
  }
]

const steps = [
  {
    icon: note,
    iconWhite: noteWhite,
    number: 1,
    text: 'Answer some quick questions about yourself and your family history'
  },
  {
    icon: health,
    iconWhite: healthWhite,
    number: 2,
    text: 'Learn about your health and risk factors'
  },
  {
    icon: checks,
    iconWhite: checksWhite,
    number: 3,
    text: 'Find out what preventative health steps might be right for you'
  }
]

const Component = styled.div`
  padding: 24px;

  @media only screen and (min-width: 768px) {
    padding: 88px 44px 96px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`

const Info = styled.div`
  margin-bottom: 32px;
  width: 100%;
  display: flex;
  flex-direction: column;

  @media only screen and (min-width: 768px) {
    margin-bottom: 64px;
    max-width: 1192px;
    flex-direction: row;
    justify-content: space-between;
    margin-bottom: 88px;
  }
`

const InfoContent = estyled.div`
  max-width: 680px;
  order: 1;

  & > h2 {
    font-size: 24px;
    line-height: 28px;
  }

  & > h6 {
    font-weight: normal;
  }

  @media only screen and (min-width: 768px) {
    margin-bottom: 2rem;

    & > h6 {
      font-weight: bold;
    }
  }
`

const ImageWrapper = estyled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;

  @media only screen and (min-width: 768px) {
    order: 2;
    margin-bottom: 0;
  }
`

const Image = estyled.img`
  width: 100%;

  @media only screen and (min-width: 768px) {
    max-width: 478px;
  }
`

const List = styled.div`
  margin-top: 12px;

  @media only screen and (min-width: 768px) {
    margin-bottom: 100px;
  }
`

const Steps = styled.div`
  margin-bottom: 80px;

  @media only screen and (min-width: 768px) {
    max-width: 1192px;
    width: 100%;
    display: flex;
    justify-content: space-between;
    margin-bottom: 0;
  }
`

const ListItem = estyled.div`
  display: flex;

  & > h6 {
    font-size: 20px;
    line-height: 32px;
  }

  &:not(:last-child) {
    margin-bottom: 24px;
  }
`

// TODO change max width to 1280px?

const NumWrapper = styled.div`
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background-color: ${() => getGelTokens().brand.brandColorPrimary1};
  width: 32px;
  height: 32px;
  margin-right: 8px;
`

const IconItem = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;
  padding: 28px;
  box-shadow: 0px 50px 77px rgba(176, 183, 195, 0.22);
  border-radius: 15px;

  @media only screen and (min-width: 768px) {
    max-width: 405px;
    margin-bottom: 0;

    &:not(:last-of-type) {
      margin-right: 24px;
    }
  }
`

const IconWrapper = styled.div`
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background-color: ${() => getGelTokens().brand.brandColorPrimary1T10};
  width: 100px;
  height: 100px;
  margin-bottom: 1rem;
`

const Icon = styled.img`
  height: 36px;
`

const TextWrapper = estyled.div`
  & > h4 {
    font-size: 28px;
    line-height: 32px;
  }

  & > h6 {
    font-size: 20px;
    line-height: 32px;
  }

  @media only screen and (min-width: 768px) {
    & > p:last-child {
      text-align: center;
    }
  }

  /* ie11 */
  @media only screen and (-ms-high-contrast: active) and (-ms-high-contrast: none) {
    max-width: 328px;
  }
`

const Content = () => {
  const { screen, isLargerThanSmScreen } = useContext(GelScreenDetectorContext)

  return (
    <Component>
      <Info>
        <InfoContent>
          <GelHeading2 marginBottom={28}>
            Helping you keep an eye on your health for a rewarding future
          </GelHeading2>
          <GelParagraph
            large
            marginBottom={20}
            style={{
              fontWeight: '400',
              fontSize: '18px',
              fontFamily: 'Pluto Sans Condensed',
              color: getGelTokens().brand.brandColorGrayT40,
              lineHeight: '20px'
            }}
          >
            Sometimes our best intentions to lead a healthy life get sidelined.
            One easy way to look after your health now and into the future is to
            take a preventative health check.
          </GelParagraph>
          <GelHeading6>Why have a preventative health check?:</GelHeading6>
          <List>
            {why.map(({ number, text }) => (
              <ListItem key={number + text}>
                <NumWrapper>
                  <GelParagraph
                    style={{
                      color: getGelTokens().brand.brandColorWhite,
                      fontWeight: '400'
                    }}
                  >
                    {number}
                  </GelParagraph>
                </NumWrapper>
                <GelHeading6
                  style={{
                    color: getGelTokens().brand.brandColorGrayT40,
                    lineHeight: '32px'
                  }}
                >
                  {text}
                </GelHeading6>
              </ListItem>
            ))}
          </List>
          {isLargerThanSmScreen(screen) && (
            <GelHeading6
              style={{
                color: getGelTokens().brand.brandColorGrayT40,
                lineHeight: '32px'
              }}
            >
              Complete the TAL Health Scout quiz to find out what checks might
              be right for you. So go on, take five for your health today.
            </GelHeading6>
          )}
        </InfoContent>
        <ImageWrapper>
          <Image src={comboImage} alt='TAL' />
        </ImageWrapper>
      </Info>
      <Steps>
        {steps.map(({ number, text, icon, iconWhite }) => (
          <IconItem key={number + text}>
            <IconWrapper>
              <Icon src={icon} alt={`Step ${number}`} />
            </IconWrapper>
            <TextWrapper>
              <GelHeading4 marginBottom={16}>Step {number}</GelHeading4>
              <GelHeading6
                style={{
                  color: getGelTokens().brand.brandColorGrayT40,
                  lineHeight: '32px'
                }}
              >
                {text}
              </GelHeading6>
            </TextWrapper>
          </IconItem>
        ))}
      </Steps>
    </Component>
  )
}

export default Content
